/**
 * Plan-change confirm dialog (ADR-0041 Fase 1). The amounts are computed BY STRIPE (the
 * proration preview, VAT via Stripe Tax) — Tray does ZERO math — so the user sees exactly
 * what will be charged before confirming. Three states, driven by the preview response:
 *
 * - loading  → spinner while `previewChangePlan` runs.
 * - blocked  → the tenant is billed but has no Stripe subscription (422) → point to the
 *              Customer Portal; no apply button.
 * - free     → billing OFF (`stripe:false`) → an immediate, no-charge switch.
 * - paid     → Stripe preview with line items + subtotal + IVA + total charged NOW.
 *
 * Presentational: the parent owns the preview/apply mutations and passes the resolved state.
 */
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { type BillingChangePreview, formatCurrency } from "@/lib/api-billing";

export interface ChangePlanState {
  planName: string;
  loading: boolean;
  blocked: boolean; // billed tenant with no Stripe sub → switch unavailable here
  error: boolean;
  preview: BillingChangePreview | null;
}

export function ChangePlanDialog({
  open,
  state,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  state: ChangePlanState;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { planName, loading, blocked, error, preview } = state;
  const cur = preview?.currency ?? "EUR";
  // DOWNGRADE → deferred to period end (no charge); the paid proration path is upgrades only.
  const isDeferred = Boolean(preview?.stripe && preview?.deferred);
  const isPaid = Boolean(preview?.stripe) && !isDeferred;
  const effectiveDate = preview?.effective_at
    ? new Date(preview.effective_at).toLocaleDateString()
    : null;
  // Confirm is offered for the free and paid cases only; never while loading / blocked / errored.
  const canConfirm = !loading && !blocked && !error && preview != null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("billing.changeTitle", "Cambia piano")}
            {planName ? ` — ${planName}` : ""}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">
              {t("billing.previewLoading", "Calcolo dell'importo con Stripe…")}
            </p>
          ) : blocked ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "billing.changeBlockedNoStripe",
                "Questo abbonamento non è gestito da Stripe. Usa “Gestisci abbonamento” o contatta il supporto per cambiare piano.",
              )}
            </p>
          ) : error ? (
            <p className="text-sm text-danger">
              {t("billing.previewError", "Impossibile calcolare l'importo. Riprova.")}
            </p>
          ) : isDeferred ? (
            <>
              <p className="text-sm text-muted-foreground">
                {effectiveDate
                  ? t("billing.deferredIntro", "Passerai al nuovo piano il {{date}}.", {
                      date: effectiveDate,
                    })
                  : t(
                      "billing.deferredIntroNoDate",
                      "Passerai al nuovo piano alla fine del periodo già pagato.",
                    )}
              </p>
              <p className="text-sm text-muted-foreground">
                {t(
                  "billing.deferredKeep",
                  "Fino ad allora mantieni tutte le funzionalità del piano attuale. Nessun addebito.",
                )}
              </p>
            </>
          ) : isPaid ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t(
                  "billing.paidIntro",
                  "Il cambio è immediato. Stripe addebita ora la differenza in proporzione (proration):",
                )}
              </p>
              {preview?.lines && preview.lines.length > 0 && (
                <ul className="space-y-1 rounded-lg border border-border/70 bg-surface/40 p-3 text-xs">
                  {preview.lines.map((ln, i) => (
                    <li key={i} className="flex items-start justify-between gap-3">
                      <span className="text-muted-foreground">{ln.description}</span>
                      <span className="shrink-0 tabular-nums">
                        {formatCurrency(ln.amount, cur)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <dl className="space-y-1 text-sm">
                <Row label={t("billing.subtotal", "Imponibile")} value={preview?.subtotal} cur={cur} />
                <Row label={t("billing.vat", "IVA")} value={preview?.tax} cur={cur} />
                <div className="flex items-center justify-between border-t border-border/60 pt-1 font-semibold">
                  <dt>{t("billing.totalNow", "Totale addebitato ora")}</dt>
                  <dd className="tabular-nums">
                    {formatCurrency(preview?.amount_due ?? preview?.total ?? 0, cur)}
                  </dd>
                </div>
              </dl>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "billing.taxNote",
                  "Importi e IVA calcolati da Stripe; l'addebito avviene sulla carta registrata.",
                )}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("billing.freeSwitch", "Il cambio piano è immediato e senza addebito.")}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            {blocked || error
              ? t("common.close", "Chiudi")
              : t("common.cancel", "Annulla")}
          </Button>
          {canConfirm && (
            <Button size="sm" onClick={onConfirm} disabled={busy}>
              {busy
                ? t("billing.switching", "Cambio in corso…")
                : isPaid
                  ? t("billing.confirmAndPay", "Conferma e paga")
                  : isDeferred
                    ? t("billing.confirmScheduled", "Programma il cambio")
                    : t("billing.confirm", "Conferma")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, cur }: { label: string; value: number | undefined; cur: string }) {
  return (
    <div className="flex items-center justify-between text-muted-foreground">
      <dt>{label}</dt>
      <dd className="tabular-nums">{formatCurrency(value ?? 0, cur)}</dd>
    </div>
  );
}
